import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { Spinner } from '@/components/Elements';
import { MainLayout } from '@/components/Layout';
import { APP_BASE_PATH } from '@/config';
import { AdditionalCompensationsRoutes } from '@/features/additionalCompensations';
import { CompanyProfile } from '@/features/company';
import { CostCalculator } from '@/features/costCalculator';
import { Settings } from '@/features/misc';
import { PaycycleRoutes } from '@/features/paycycle';
import { ReportsRoutes } from '@/features/reports';
import { TimeOffRoutes } from '@/features/timeOff';
import { UsersDashboard } from '@/features/users';
import { TeamDashboard, WorkerRoutes } from '@/features/workers';
import { Authorization, ROLES } from '@/lib/authorization';

const App = () => (
  <MainLayout>
    <Suspense
      fallback={
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  </MainLayout>
);

const ForbiddenFallback = () => (
  <Authorization allowedRoles={[ROLES.viewer]} forbiddenFallback={<Navigate to={'/app/team'} />}>
    <Navigate to={'/app/paycycle'} />
  </Authorization>
);

export const protectedRoutes = [
  {
    path: `${APP_BASE_PATH}/*`,
    element: <App />,
    children: [
      {
        path: 'company',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin]}
          >
            <CompanyProfile />
          </Authorization>
        ),
      },
      {
        path: 'team',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.manager]}
          >
            <TeamDashboard />
          </Authorization>
        ),
      },
      {
        path: 'additional-compensation/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.manager]}
          >
            <AdditionalCompensationsRoutes />
          </Authorization>
        ),
      },
      {
        path: 'time-off/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.manager]}
          >
            <TimeOffRoutes />
          </Authorization>
        ),
      },
      {
        path: 'cost-calculator',
        element: <CostCalculator />,
      },
      {
        path: 'reports/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.viewer]}
          >
            <ReportsRoutes />
          </Authorization>
        ),
      },
      {
        path: 'paycycle/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.viewer]}
          >
            <PaycycleRoutes />
          </Authorization>
        ),
      },
      {
        path: 'users/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin]}
          >
            <UsersDashboard />
          </Authorization>
        ),
      },
      {
        path: 'team/worker/*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.manager]}
          >
            <WorkerRoutes />
          </Authorization>
        ),
      },
      { path: 'settings', element: <Settings /> },
      {
        path: '*',
        element: (
          <Authorization
            forbiddenFallback={<ForbiddenFallback />}
            allowedRoles={[ROLES.superAdmin, ROLES.admin, ROLES.manager]}
          >
            <Navigate to={`${APP_BASE_PATH}/team`} />
          </Authorization>
        ),
      },
    ],
  },
  {
    path: '/*',
    element: <Navigate to={`${APP_BASE_PATH}`} />,
  },
];
